

/**
 * SubmitURLModal
 *
 * Reusable modal for reward claims that require URL submission as proof
 * (TikTok video posted, Tweet linking us, IG post tagged, etc.).
 *
 * Flow:
 *   1. User clicks "Claim" on a social/content reward in /earn
 *   2. RewardsList opens this modal with the reward + placeholder URL
 *   3. User opens the platform via "Open <Platform>" button (optional convenience)
 *   4. User pastes their post URL and submits
 *   5. Modal calls onSubmit(url) — RewardsList POSTs to /api/rewards/claim
 *      with `data: { postUrl }`
 *   6. Verifier server-side validates URL (or returns NOT_IMPLEMENTED until live)
 *
 * Validation here is shape-only (does it look like a URL on the right domain).
 * Real verification lives in the platform verifier on the server.
 */

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

export interface SubmitURLModalReward {
  rewardType: string;
  label: string;
  description: string;
  coinsAmount: number;
  /** What kind of platform — drives placeholder text + URL host validation */
  platform: 'twitter' | 'instagram' | 'tiktok' | 'generic';
  /** Optional URL the user might want to open first (their "compose post" link) */
  composeUrl?: string;
  /** Optional copy hint shown above the input — e.g. "Tag @FantasyPRIME in your caption" */
  hint?: string;
}

export interface SubmitURLModalProps {
  reward: SubmitURLModalReward | null;
  open: boolean;
  submitting: boolean;
  /** When non-null, displayed as a top error banner inside the modal */
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (postUrl: string) => void | Promise<void>;
}

const HOST_PATTERNS: Record<SubmitURLModalReward['platform'], RegExp> = {
  twitter: /^https?:\/\/(www\.)?(twitter\.com|x\.com)\//i,
  instagram: /^https?:\/\/(www\.)?instagram\.com\//i,
  tiktok: /^https?:\/\/(www\.)?tiktok\.com\//i,
  generic: /^https?:\/\//i,
};

const PLATFORM_LABEL: Record<SubmitURLModalReward['platform'], string> = {
  twitter: 'X (Twitter)',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  generic: 'the platform',
};

const PLATFORM_PLACEHOLDER: Record<SubmitURLModalReward['platform'], string> = {
  twitter: 'https://x.com/yourhandle/status/...',
  instagram: 'https://instagram.com/p/... or /stories/...',
  tiktok: 'https://www.tiktok.com/@yourhandle/video/...',
  generic: 'https://...',
};

const MAX_URL_LENGTH = 512;

function isValidUrl(url: string, platform: SubmitURLModalReward['platform']): boolean {
  if (url.length === 0 || url.length > MAX_URL_LENGTH) return false;
  if (!HOST_PATTERNS[platform].test(url)) return false;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

function CoinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v12M9 9.5c0-.83 1.34-1.5 3-1.5s3 .67 3 1.5S14.66 11 12 11 9 11.67 9 12.5s1.34 1.5 3 1.5 3-.67 3-1.5" />
    </svg>
  );
}

function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export function SubmitURLModal({ reward, open, submitting, errorMessage, onClose, onSubmit }: SubmitURLModalProps) {
  const [url, setUrl] = useState('');
  const [touched, setTouched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setUrl('');
      setTouched(false);
      // Autofocus the URL input shortly after the modal mounts
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, reward?.rewardType]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  if (!open || !reward) return null;

  const trimmed = url.trim();
  const valid = isValidUrl(trimmed, reward.platform);
  const showInvalid = touched && trimmed.length > 0 && !valid;

  function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    setTouched(true);
    if (!valid || submitting) return;
    void onSubmit(trimmed);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="submit-url-modal-title"
      onClick={(e) => {
        // Click the backdrop (not the dialog itself) to close
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-xl bg-surface-2 border border-surface-3 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-surface-3">
          <div className="flex-1 min-w-0">
            <h2 id="submit-url-modal-title" className="text-sm font-bold text-cream truncate">
              {reward.label}
            </h2>
            <p className="text-[11px] text-warm-gray/60 mt-0.5">{reward.description}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-3">
            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-gold bg-gold/15 border border-gold/30 rounded-full px-2 py-0.5">
              <CoinIcon className="w-3 h-3" /> +{reward.coinsAmount}
            </span>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              aria-label="Close"
              className="text-warm-gray/60 hover:text-cream disabled:opacity-50"
            >
              <CloseIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {reward.hint && (
            <div className="text-[11px] text-warm-gray/70 bg-surface-3/50 border border-surface-3 rounded-lg p-2.5 leading-relaxed">
              {reward.hint}
            </div>
          )}

          {reward.composeUrl && (
            <a
              href={reward.composeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-gold hover:underline"
            >
              <ExternalLinkIcon className="w-3 h-3" />
              Open {PLATFORM_LABEL[reward.platform]}
            </a>
          )}

          <div>
            <label htmlFor="post-url" className="block text-[10px] font-medium uppercase tracking-wider text-warm-gray/60 mb-1.5">
              Paste your post URL
            </label>
            <input
              ref={inputRef}
              id="post-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder={PLATFORM_PLACEHOLDER[reward.platform]}
              disabled={submitting}
              maxLength={MAX_URL_LENGTH}
              autoComplete="off"
              spellCheck={false}
              className={cn(
                'w-full bg-surface-3 border rounded-lg px-3 py-2 text-[12px] text-cream font-mono placeholder:text-warm-gray/30 focus:outline-none focus:ring-2 focus:ring-gold/40 transition-colors',
                showInvalid ? 'border-oxblood/50' : 'border-surface-4',
              )}
            />
            {showInvalid && (
              <p className="text-[10px] text-oxblood/80 mt-1">
                Please paste a valid {PLATFORM_LABEL[reward.platform]} URL
              </p>
            )}
          </div>

          {errorMessage && (
            <div className="text-[11px] text-oxblood bg-oxblood/10 border border-oxblood/30 rounded-lg p-2">
              {errorMessage}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-warm-gray hover:text-cream disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!valid || submitting}
              className={cn(
                'px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all',
                valid && !submitting
                  ? 'bg-gold/15 text-gold hover:bg-gold/25 border border-gold/30'
                  : 'bg-surface-3 text-warm-gray/40 border border-transparent cursor-not-allowed',
              )}
            >
              {submitting ? 'Submitting…' : `Submit for +${reward.coinsAmount}c`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Exported for tests
export { isValidUrl };

export default SubmitURLModal;
